import React, { memo, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import useWindowIsMobile from '@/utils/hooks/useWindowIsMobile';

import { TextContext } from '@/utils/contexts/TextContext';
import { ModalContext } from '@/utils/contexts/ModalContext';

import LetterContentSimplificationMenu from '@/views/pages/Letter/elements/LetterContentSimplificationMenu';

import LetterContent from '@/views/pages/Letter/elements/LetterContent';

import Tab from '@/views/components/Tab/Tab';
import SimpleAlert from '@/views/components/Alert/Alert';
import TooltipIcon from '@/views/components/TooltipIcon/TooltipIcon';

import ExplanationPanel from '@/views/components/ExplanationPanel/ExplanationPanel';
import LetterFinishSimplificationBar from '@/views/pages/Letter/elements/LetterFinishSimplificationBar';
import LetterContinueSimplificationBar from '@/views/pages/Letter/elements/LetterContinueSimplificationBar';

const LetterContentWrapper = ({ modes, rightModes, currentMode, setCurrentMode}) => {
	const { dict, langNormalization } = useSelector((state) => state.app);
	const { user } = useSelector((state) => state.auth);

	const [ availableModes, setAvailableModes ] = useState(null);
	const [ isExplanationVisible, setExplanationVisible ] = useState(false); 

	const isMobile = useWindowIsMobile(991);

	const { contextState, contextDispatch } = useContext(TextContext);
	const { showModal } = useContext(ModalContext);
	// const [characters, setCharacters] = useState(null);
	
	// first get data and set all availeble modes
	useEffect(() => {
		// map props letters modes with incoming modes
		if (modes && contextState.letterData) {
			setAvailableModes(modes.filter(it => {
				return (contextState.letterData[it]);
			}));
		}
	}, [ contextState.letterData ]);

	// set current mode to first available
	useEffect(() => {
		if (availableModes) {
			setCurrentMode(availableModes[0]);
		}
	}, [ availableModes ]);

	useEffect(() => {
		if (user.displaySimplifyingGuide) {
			showModal({
				componentName: 'ModalSimplification',
			}); 
		}
	}, []);

	const toggleExplanation = () => {
		setExplanationVisible(!isExplanationVisible);
	};

	return (
		<div className='letter__text--container'>
			<div className='letter__text--header noselect'>
				<div className='letter__text--mode-selector'>
					{ modes.includes('originalDoc') && <Tab 
						isBtn={ false }
						disabled={ availableModes && !availableModes.includes('originalDoc') }
						isTabActive={ 'originalDoc' === currentMode }
						onClickHandler={ setCurrentMode }
						value='originalDoc'
						label={ dict.letter.originalDoc }
					/> }
					{ modes.includes('simplifiedDoc') && <Tab 
						isBtn={ true }
						disabled={ availableModes && !availableModes.includes('simplifiedDoc') }
						isTabActive={ 'simplifiedDoc' === currentMode }
						onClickHandler={ setCurrentMode }
						value='simplifiedDoc'
						label={ dict.letter.simplifiedDoc }
					/> }
					{ modes.includes('finalDoc') && <Tab 
						isBtn={ true }
						disabled={ availableModes && !availableModes.includes('finalDoc') }
						isTabActive={ 'finalDoc' === currentMode }
						onClickHandler={ setCurrentMode }
						value='finalDoc'
						label={ dict.letter.finalDoc }
					/> }
					{ rightModes && rightModes.includes('finalDoc') && (
						<div className='letter__text--tooltip-icon'>
							<TooltipIcon color={ "#2559C0" }/>
							<span className='letter__text--tooltip-text'>
								<b>Tekst roboczy</b> prezentuje na bieżąco zmiany (redaktorskie i automatyczne) wprowadzone w tekście oryginalnym (po lewej). <b>Tekst ostateczny</b> prezentuje tekst po zakończeniu upraszczania. Jest to osobny plik lub zatwierdzona wersja robocza.
							</span>
						</div>
					)} 
				</div>
				<div className='letter__text--info'>
					<div>
						<span style={ { fontWeight: '500' } }>{ isMobile ? dict.letter.charactersMobile : dict.letter.characters}&nbsp;</span>{(currentMode && contextState.letterData[currentMode]?.characters) ? contextState.letterData[currentMode].characters : 0}
					</div>
					<div className='letter__text--header-btns'>
						{!isMobile && currentMode === 'originalDoc' && contextState.letterData.version !== 'final' && (user && user.role !== 'reader') && (
							<>
								<TooltipIcon 
									color="#0066d3"
									tooltipText={ `${dict.letter.contentSimplificationMenu.tooltip.simplifications}${langNormalization ? ` ${dict.letter.contentSimplificationMenu.tooltip.corrections}` : '' }` }
								/>
								<LetterContentSimplificationMenu
									onAddAutoChanges={ () => contextDispatch({ type: 'suggestAutomaticChanges' }) }
									onAddAutoFixes={ () => contextDispatch({ type: 'suggestAutomaticFixes' }) }
									onClearAutoChanges={ () => contextDispatch({ type: 'removeAutomaticChanges' }) }
									autoChanges={ contextState.letterData.autoChanges }
								/>
							</>
						)}
						<div
							className='letter__text--header-btn--download'
							onClick={ () => contextDispatch({
								type: 'handleDownloadFile', payload: {
									currentMode,
								},
							}) }
						>
							{currentMode === 'originalDoc' ? dict.downloadOriginal : dict.downloadWorkingText}
						</div>
					</div>
				</div>
			</div>
			{!isMobile && currentMode === 'originalDoc' && (
				<>
					<div>
						{isExplanationVisible && <ExplanationPanel isVisible={ isExplanationVisible } />}
						<div 
							className='letter__text--explanation-toggle' 
							onClick={ toggleExplanation }
						>
							{isExplanationVisible ? <>Zwiń wyjaśnienie oznaczeń<i className='icon-arrow-up-full' /></> : <>Rozwiń wyjaśnienie oznaczeń<i className='icon-arrow-down-full' /></>}
						</div>
					</div>
				</>
			)}
			{currentMode === 'finalDoc' && contextState.letterData.version !== 'final' && (
				<SimpleAlert 
					variant='warning'
					className='letter__text--final-text-warning'
					alertContent={ <><span style={ { fontWeight: '500' } }>Uwaga:</span> To jest nieaktualna wersja. Tekst ostateczny zostanie zaktualizowany po zakończeniu upraszczania.</> }
				/>
			)}
			{!isMobile && currentMode === 'originalDoc' && contextState.letterData.version === 'final' && contextState.letterData?.rights?.canEdit && (
				<LetterContinueSimplificationBar />
			)}
			<LetterContent mode={ currentMode } />
			{!isMobile && currentMode === 'originalDoc' && contextState.letterData.version !== 'final' && contextState.letterData?.rights?.canEdit && (
				<LetterFinishSimplificationBar />
			)}
		</div>
	);
};
export default memo(LetterContentWrapper);